import React from 'react';
import {ConstraintDataPlugin, ConstraintPluginProps} from '../ConstraintDataPlugin';
import {
    ConstraintType,
    DataTypeConstraintDto,
    DateRangeConstraintDataDto
} from '../../../../../../common/apis/datatype/DataTypeConstraintDto';
import {CoreDataType} from "../../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {DataTypeTranslationKey} from "../../DataTypeTranslationKey";
import {DateRangeConstraintPluginForm} from "./DateRangeConstraintPluginForm";
import {ConstraintValueValidationPlugin} from "../ConstraintValueValidationPlugin";
import {TranslationFunction} from "../../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../../CommonTranslation";
import {StereotypesTranslationKey} from "../../../stereotypes/StereotypesTranslationKey";
import dayjs from 'dayjs';
import {DateTimeFormatter} from "../../../../../../common/DateTimeFormatter";


export class DateRangeConstraintPlugin implements ConstraintDataPlugin<DateRangeConstraintDataDto>, ConstraintValueValidationPlugin {
    readonly type = ConstraintType.DATE_RANGE;

    getSupportedDataTypes(): CoreDataType[] {
        return [
            CoreDataType.DATE
        ];
    }

    getInitialData(): DateRangeConstraintDataDto {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        return {
            type: ConstraintType.DATE_RANGE,
            dateFrom: DateTimeFormatter.formatAsIsoDate(today),
            dateTo: DateTimeFormatter.formatAsIsoDate(tomorrow)
        };
    }

    render(props: ConstraintPluginProps<DateRangeConstraintDataDto>): React.ReactElement {
        return <DateRangeConstraintPluginForm {...props} />;
    }

    validate(data: DateRangeConstraintDataDto, coreDataType: CoreDataType): Record<string, string> {
        const errors: Record<string, string> = {};

        if ((data.dateFrom === null || data.dateFrom === undefined) && (data.dateTo === null || data.dateTo === undefined)) {
            errors.min = DataTypeTranslationKey.AT_LEAST_ONE_OF_MIN_OR_MAX_MUST_BE_SPECIFIED;
        } else if (data.dateFrom && data.dateTo && data.dateFrom > data.dateTo) {
            errors.min = DataTypeTranslationKey.MIN_VALUE_MUST_BE_LESS_THAN_OR_EQUAL_TO_MAX;
        }

        return errors;
    }

    validateValue(value: any, attributeId: string, constraint: DataTypeConstraintDto, t: TranslationFunction): Record<string, string> {
        const constraintData = constraint.constraintData as DateRangeConstraintDataDto;
        const {dateFrom, dateTo} = constraintData;

        if (!value) {
            return {
                [attributeId]: t(CommonTranslation.FILL_OUT_THIS_FIELD)
            };
        }

        const convertedValue = dayjs(value);
        if (!convertedValue.isValid()) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_DATE), attributeId);
        }

        const convertedDateFrom = dateFrom ? dayjs(dateFrom) : null;
        const convertedDateTo = dateTo ? dayjs(dateTo) : null;

        if (convertedDateFrom && convertedValue.isBefore(convertedDateFrom)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_BELLOW_ALLOWED_LIMIT), attributeId);
        }

        if (convertedDateTo && convertedValue.isAfter(convertedDateTo)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_ABOVE_ALLOWED_LIMIT), attributeId);
        }

        return {};
    }

    private errorResult(error: string, attributeId: string) {
        return {
            [attributeId]: error
        };
    }

    getConstraintExpression(data: DateRangeConstraintDataDto): string {
        const { dateFrom, dateTo } = data;
        if (dateFrom === undefined && dateTo === undefined) {
            return "";
        }

        const left = DateTimeFormatter.formatDate(dateFrom) ?? "-∞";
        const right = DateTimeFormatter.formatDate(dateTo) ?? "∞";

        return `<${left} - ${right}>`;
    }
}
